import { Workout, Block, IntervalSet, Ramp } from '../types/workout';

interface WorkoutSegment {
  startTime: number;
  endTime: number;
  power: number;
  label: string;
}

export class WorkoutVisualization {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width = 1200;
  private height = 600;
  private margin = { top: 80, right: 50, bottom: 80, left: 80 };

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.ctx = this.canvas.getContext('2d')!;
  }

  // Convert workout sections to a timeline of power segments
  private convertWorkoutToSegments(workout: Workout): WorkoutSegment[] {
    const segments: WorkoutSegment[] = [];
    let currentTime = 0;

    workout.sections.forEach((section) => {
      if (section.type === 'block') {
        const block = section as Block;
        segments.push({
          startTime: currentTime,
          endTime: currentTime + block.duration,
          power: block.targetPower,
          label: block.name
        });
        currentTime += block.duration;
      } else if (section.type === 'ramp') {
        const ramp = section as Ramp;
        // For ramps, create multiple segments to show the progression
        const steps = Math.max(10, Math.floor(ramp.duration / 10)); // At least 10 steps or 1 per 10 seconds
        const stepDuration = ramp.duration / steps;
        const powerDiff = ramp.endPower - ramp.startPower;
        
        for (let i = 0; i < steps; i++) {
          const progress = i / (steps - 1);
          const power = ramp.startPower + (powerDiff * progress);
          segments.push({
            startTime: currentTime + (i * stepDuration),
            endTime: currentTime + ((i + 1) * stepDuration),
            power: power,
            label: i === 0 ? ramp.name : ''
          });
        }
        currentTime += ramp.duration;
      } else if (section.type === 'interval') {
        const intervalSet = section as IntervalSet;
        for (let rep = 0; rep < intervalSet.repeats; rep++) {
          // Work interval
          segments.push({
            startTime: currentTime,
            endTime: currentTime + intervalSet.onInterval.duration,
            power: intervalSet.onInterval.targetPower,
            label: rep === 0 ? `${intervalSet.name} (Work)` : ''
          });
          currentTime += intervalSet.onInterval.duration;

          // Rest interval
          segments.push({
            startTime: currentTime,
            endTime: currentTime + intervalSet.restInterval.duration,
            power: intervalSet.restInterval.targetPower,
            label: rep === 0 ? `${intervalSet.name} (Rest)` : ''
          });
          currentTime += intervalSet.restInterval.duration;
        }
      }
    });

    return segments;
  }

  // Format time as MM:SS
  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // Generate the workout visualization
  generateWorkoutChart(workout: Workout): Promise<Blob> {
    return new Promise((resolve) => {
      // Clear canvas with white background
      this.ctx.fillStyle = '#ffffff';
      this.ctx.fillRect(0, 0, this.width, this.height);

      const segments = this.convertWorkoutToSegments(workout);
      if (segments.length === 0) {
        resolve(new Blob()); // Return empty blob if no segments
        return;
      }

      const totalDuration = segments[segments.length - 1].endTime;
      const maxPower = Math.max(...segments.map(s => s.power));
      const chartWidth = this.width - this.margin.left - this.margin.right;
      const chartHeight = this.height - this.margin.top - this.margin.bottom;

      // Draw title
      this.ctx.fillStyle = '#1a1a1a';
      this.ctx.font = 'bold 24px Arial';
      this.ctx.textAlign = 'center';
      this.ctx.fillText('turbo-trainer.com', this.width / 2, 30);

      this.ctx.font = '16px Arial';
      this.ctx.fillText('Workout Power Profile', this.width / 2, 55);

      // Draw axes
      this.ctx.strokeStyle = '#333333';
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      // Y-axis
      this.ctx.moveTo(this.margin.left, this.margin.top);
      this.ctx.lineTo(this.margin.left, this.height - this.margin.bottom);
      // X-axis
      this.ctx.moveTo(this.margin.left, this.height - this.margin.bottom);
      this.ctx.lineTo(this.width - this.margin.right, this.height - this.margin.bottom);
      this.ctx.stroke();

      // Draw power bars
      segments.forEach((segment) => {
        const x = this.margin.left + (segment.startTime / totalDuration) * chartWidth;
        const width = ((segment.endTime - segment.startTime) / totalDuration) * chartWidth;
        const height = (segment.power / maxPower) * chartHeight;
        const y = this.height - this.margin.bottom - height;

        // Use different colors based on power zones (rough approximation)
        let color = '#3b82f6'; // Default blue
        if (segment.power < maxPower * 0.3) color = '#10b981'; // Easy - green
        else if (segment.power < maxPower * 0.6) color = '#f59e0b'; // Moderate - yellow
        else if (segment.power < maxPower * 0.8) color = '#ef4444'; // Hard - red
        else color = '#8b5cf6'; // Very hard - purple

        this.ctx.fillStyle = color;
        this.ctx.fillRect(x, y, width, height);

        // Draw border
        this.ctx.strokeStyle = '#ffffff';
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(x, y, width, height);

        // Add power labels on bars if wide enough
        if (width > 30) {
          this.ctx.fillStyle = '#ffffff';
          this.ctx.font = 'bold 12px Arial';
          this.ctx.textAlign = 'center';
          this.ctx.fillText(
            `${Math.round(segment.power)}W`,
            x + width / 2,
            y + height / 2 + 4
          );
        }
      });

      // Draw Y-axis labels (power)
      this.ctx.fillStyle = '#333333';
      this.ctx.font = '12px Arial';
      this.ctx.textAlign = 'right';
      for (let i = 0; i <= 5; i++) {
        const power = (maxPower / 5) * i;
        const y = this.height - this.margin.bottom - (power / maxPower) * chartHeight;
        this.ctx.fillText(`${Math.round(power)}W`, this.margin.left - 10, y + 4);
        
        // Draw grid lines
        this.ctx.strokeStyle = '#e5e5e5';
        this.ctx.lineWidth = 1;
        this.ctx.beginPath();
        this.ctx.moveTo(this.margin.left, y);
        this.ctx.lineTo(this.width - this.margin.right, y);
        this.ctx.stroke();
      }

      // Draw X-axis labels (time)
      this.ctx.textAlign = 'center';
      const timeSteps = Math.min(8, Math.ceil(totalDuration / 300)); // Max 8 labels, at least every 5 minutes
      for (let i = 0; i <= timeSteps; i++) {
        const time = (totalDuration / timeSteps) * i;
        const x = this.margin.left + (time / totalDuration) * chartWidth;
        this.ctx.fillText(this.formatTime(Math.round(time)), x, this.height - this.margin.bottom + 20);
        
        // Draw grid lines
        this.ctx.strokeStyle = '#e5e5e5';
        this.ctx.lineWidth = 1;
        this.ctx.beginPath();
        this.ctx.moveTo(x, this.margin.top);
        this.ctx.lineTo(x, this.height - this.margin.bottom);
        this.ctx.stroke();
      }

      // Add axis labels
      this.ctx.fillStyle = '#333333';
      this.ctx.font = 'bold 14px Arial';
      this.ctx.textAlign = 'center';
      
      // X-axis label
      this.ctx.fillText('Time', this.width / 2, this.height - 25);
      
      // Y-axis label (rotated)
      this.ctx.save();
      this.ctx.translate(25, this.height / 2);
      this.ctx.rotate(-Math.PI / 2);
      this.ctx.fillText('Power (Watts)', 0, 0);
      this.ctx.restore();

      // Add workout summary
      const totalTime = this.formatTime(totalDuration);
      const avgPower = Math.round(
        segments.reduce((sum, seg) => sum + seg.power * (seg.endTime - seg.startTime), 0) / totalDuration
      );
      
      this.ctx.fillStyle = '#666666';
      this.ctx.font = '12px Arial';
      this.ctx.textAlign = 'left';
      this.ctx.fillText(`Duration: ${totalTime}`, this.margin.left, this.height - 45);
      this.ctx.fillText(`Avg Power: ${avgPower}W`, this.margin.left + 120, this.height - 45);
      this.ctx.fillText(`Max Power: ${Math.round(maxPower)}W`, this.margin.left + 240, this.height - 45);

      // Convert canvas to blob
      this.canvas.toBlob((blob) => {
        resolve(blob || new Blob());
      }, 'image/png', 0.9);
    });
  }
}

// Utility function to generate workout chart
export async function generateWorkoutChart(workout: Workout): Promise<Blob> {
  const visualizer = new WorkoutVisualization();
  return visualizer.generateWorkoutChart(workout);
}